import { jsxs, jsx } from "react/jsx-runtime";
import { useState, useRef, useCallback, useEffect } from "react";
import { Icon } from "@zapier/zinnia-icons";
import styles from "./LocaleSelector.module.scss.js";
import { useHeaderContext } from "../Header.js";
import { fetchLocalizationData } from "../../../data/cdn.js";
import { pathWithoutLocale, localeFromPath, pathHasLocalizedVariant } from "../../../utils/locale.js";
import cn from "clsx";
let localizationPromise = null;
function useLocalStorage(key, initialValue = null) {
  const [storedValue, setStoredValue] = useState(() => {
    if (typeof localStorage === "undefined") return initialValue;
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });
  const setValue = (value) => {
    try {
      setStoredValue(value);
      if (typeof localStorage !== "undefined") {
        if (value === null) {
          localStorage.removeItem(key);
        } else {
          localStorage.setItem(key, JSON.stringify(value));
        }
      }
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  };
  return [storedValue, setValue];
}
function LocaleSelector(props) {
  const [isOpen, setIsOpen] = useState(false);
  const context = useHeaderContext();
  const containerRef = useRef(null);
  const [isClient, setIsClient] = useState(false);
  const [localizationPreference, setLocalizationPreference] = useLocalStorage("localizationPreference");
  const handleEvent = useCallback(
    (action, text, interactionName) => {
      context.emitUserInteractionEvent({
        event_action: action,
        interaction_name: interactionName || `locale_selector_${action}`,
        interaction_raw_text: text
      });
    },
    [context]
  );
  const handleToggle = (text, interactionName) => {
    setIsOpen((prevIsOpen) => {
      const newIsOpen = !prevIsOpen;
      if (newIsOpen) {
        handleEvent("open", text, interactionName);
      } else {
        handleEvent(
          "close",
          text,
          interactionName
        );
      }
      return newIsOpen;
    });
  };
  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleToggle(localizationPreference == null ? void 0 : localizationPreference.name);
    } else if (event.key === "Escape" && isOpen) {
      setIsOpen(false);
      handleEvent("close", localizationPreference == null ? void 0 : localizationPreference.name);
    }
  };
  const [localizedPathCollection, setLocalizedPathCollection] = useState(null);
  const [currentPath, setCurrentPath] = useState(null);
  const [currentPathWithoutLocale, setCurrentPathWithoutLocale] = useState(null);
  const [currentPathParams, setCurrentPathParams] = useState(
    null
  );
  const [pathLocale, setPathLocale] = useState(null);
  useEffect(() => {
    setIsClient(true);
    const path = window.location.pathname;
    const pathParams = window.location.search;
    const pathWithoutLocaleValue = pathWithoutLocale(path, props.locales);
    const locale = localeFromPath(path, props.locales);
    setCurrentPath(path);
    setCurrentPathWithoutLocale(pathWithoutLocaleValue);
    setCurrentPathParams(pathParams);
    setPathLocale(locale || null);
    if (locale && !localizationPreference) {
      setLocalizationPreference(locale);
    }
  }, [props.locales, localizationPreference, setLocalizationPreference]);
  useEffect(() => {
    if (!localizationPreference) return;
    if (!currentPath) return;
    if (!localizedPathCollection) return;
    if (localizationPreference.abbreviation === "en") return;
    if (pathLocale) return;
    if (pathHasLocalizedVariant(
      currentPath,
      localizationPreference,
      localizedPathCollection
    )) {
      window.location.replace(
        `/${localizationPreference.abbreviation}${currentPath}${currentPathParams}`
      );
    }
  }, [
    currentPath,
    currentPathParams,
    localizationPreference,
    pathLocale,
    localizedPathCollection
  ]);
  useEffect(() => {
    if (!localizationPreference) return;
    if (!currentPathWithoutLocale) return;
    if (!localizedPathCollection) return;
    if (localizationPreference.abbreviation === "en" && !pathLocale) {
      return;
    }
    if (localizationPreference.abbreviation === "en" && (pathLocale == null ? void 0 : pathLocale.abbreviation) === "en") {
      window.location.replace(
        `${currentPathWithoutLocale}${currentPathParams}`
      );
      return;
    }
    if ((pathLocale == null ? void 0 : pathLocale.abbreviation) && !pathHasLocalizedVariant(
      currentPathWithoutLocale,
      localizationPreference,
      localizedPathCollection
    )) {
      window.location.replace(
        `${currentPathWithoutLocale}${currentPathParams}`
      );
    }
    if (pathLocale && pathLocale.abbreviation === localizationPreference.abbreviation) {
      return;
    }
    if (pathHasLocalizedVariant(
      currentPathWithoutLocale,
      localizationPreference,
      localizedPathCollection
    )) {
      window.location.replace(
        `/${localizationPreference.abbreviation}${currentPathWithoutLocale}${currentPathParams}`
      );
    }
  }, [
    currentPathWithoutLocale,
    currentPathParams,
    localizationPreference,
    pathLocale,
    localizedPathCollection
  ]);
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      var _a;
      if ((_a = containerRef.current) == null ? void 0 : _a.contains(event.target)) return;
      setIsOpen(false);
      handleEvent("close", localizationPreference == null ? void 0 : localizationPreference.name);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isOpen, localizationPreference == null ? void 0 : localizationPreference.name, handleEvent]);
  const isDisabled = (locale) => {
    if (!currentPathWithoutLocale || !localizedPathCollection) return true;
    return !pathHasLocalizedVariant(
      currentPathWithoutLocale,
      locale,
      localizedPathCollection
    );
  };
  useEffect(() => {
    if (!localizationPromise) {
      localizationPromise = fetchLocalizationData({
        baseURL: props.baseURL,
        staging: props.staging
      });
    }
    const handleData = (localizedPathCollection2) => setLocalizedPathCollection(localizedPathCollection2);
    localizationPromise.then(handleData).catch(
      (err) => console.error("[@zapier/marketing-site-layout]", err)
    );
  }, [props.baseURL, props.staging]);
  const localeSelectorHref = (locale) => {
    if (!currentPathWithoutLocale) return "#";
    if (locale.abbreviation === "en") {
      return `${currentPathWithoutLocale}${currentPathParams}`;
    }
    return `/${locale.abbreviation}${currentPathWithoutLocale}${currentPathParams}`;
  };
  const getDisplayLocaleAbbreviation = () => {
    if (!localizedPathCollection) return "";
    if (!currentPathWithoutLocale || !localizationPreference) return "EN";
    if (pathHasLocalizedVariant(
      currentPathWithoutLocale,
      localizationPreference,
      localizedPathCollection
    ))
      return (pathLocale == null ? void 0 : pathLocale.abbreviation.toUpperCase()) || "EN";
    return "EN";
  };
  if (!isClient) return null;
  if (!localizedPathCollection) return null;
  if (!localizationPreference) {
    return null;
  }
  return /* @__PURE__ */ jsxs("div", { className: cn(styles.root, isOpen && styles.open), children: [
    /* @__PURE__ */ jsx(
      "button",
      {
        ref: containerRef,
        role: "button",
        className: styles.button,
        "data-testid": "locale-selector",
        "aria-label": `Locale selector, current preferred locale: ${localizationPreference == null ? void 0 : localizationPreference.name}, displayed locale: ${getDisplayLocaleAbbreviation()}`,
        "aria-expanded": isOpen,
        onKeyDown: handleKeyDown,
        onClick: () => handleToggle(localizationPreference == null ? void 0 : localizationPreference.name),
        children: /* @__PURE__ */ jsxs("span", { className: styles.buttonContent, children: [
          /* @__PURE__ */ jsx("span", { className: styles.label, translate: "no", children: getDisplayLocaleAbbreviation() }),
          /* @__PURE__ */ jsx("span", { className: cn(styles.arrow, isOpen && styles.rotated), children: /* @__PURE__ */ jsx(Icon, { name: "arrowSmallDown", size: 16 }) })
        ] })
      }
    ),
    /* @__PURE__ */ jsx("ul", { role: "listbox", className: cn(styles.list, isOpen && styles.open), children: props.locales.map((locale) => /* @__PURE__ */ jsx("li", { role: "none", className: styles.listItemWrapper, children: /* @__PURE__ */ jsx(
      "a",
      {
        role: "option",
        className: cn(
          styles.listItem,
          isDisabled(locale) && styles.disabled,
          "sl_opaque"
        ),
        "aria-label": `Switch to ${locale.name}`,
        onClick: () => setLocalizationPreference(locale),
        href: localeSelectorHref(locale),
        children: /* @__PURE__ */ jsx(
          "span",
          {
            className: cn(
              styles.label,
              isDisabled(locale) && styles.unavailable
            ),
            translate: "no",
            children: `${locale.name} - ${locale.abbreviation.toUpperCase()}`
          }
        )
      }
    ) }, locale.name)) })
  ] });
}
export {
  LocaleSelector
};
